import { useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Users, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_name: "", phone: "", address: "", cargo_company: "", note: "" };

export default function Customers() {
  const { customers, refreshCustomers } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, contact_name: c.contact_name || "", phone: c.phone, address: c.address, cargo_company: c.cargo_company || "", note: c.note });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Müşteri adı zorunlu"); return; }
    setSubmitting(true);
    try {
      if (editingId) await api.put(`/customers/${editingId}`, form);
      else await api.post("/customers", form);
      toast.success(editingId ? "Müşteri kartı güncellendi" : "Müşteri kartı oluşturuldu");
      setForm(empty); setOpen(false); setEditingId(null); refreshCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Müşteriyi silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Silindi");
      refreshCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="p-5 lg:p-10 max-w-[1400px]" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-[#B45309] mb-2">Müşteri Kartları</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight flex items-center gap-3">
            <Users className="w-7 h-7 text-[#D97706]" /> Müşteriler
          </h1>
          <p className="text-[#6B5B4A] mt-2 text-sm">Sipariş girerken müşteri bu listeden seçilir.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="customer-new-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> Yeni Müşteri
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-xl" data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl uppercase tracking-tight">{editingId ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs text-[#6B5B4A]">Müşteri Adı *</Label>
                <Input data-testid="customer-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B5B4A]">Yetkili Adı Soyadı</Label>
                <Input data-testid="customer-contact-name" value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Örn: Ahmet Yılmaz" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B5B4A]">Kargo Firması</Label>
                <Input data-testid="customer-cargo-company" value={form.cargo_company} list="cargo-companies"
                  onChange={(e) => setForm({ ...form, cargo_company: e.target.value })}
                  placeholder="Örn: Yurtiçi Kargo" className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
                <datalist id="cargo-companies">
                  {["Yurtiçi Kargo", "Aras Kargo", "MNG Kargo", "PTT Kargo", "Sürat Kargo", "UPS", "DHL", "Ambar"].map((k) => <option key={k} value={k} />)}
                </datalist>
              </div>
              <div>
                <Label className="text-xs text-[#6B5B4A]">Telefon</Label>
                <Input data-testid="customer-phone" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
              <div>
                <Label className="text-xs text-[#6B5B4A]">Adres</Label>
                <Input data-testid="customer-address" value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-[#6B5B4A]">Not</Label>
                <Input data-testid="customer-note" value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={submitting} data-testid="customer-submit-btn"
                className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
                {submitting ? "Kaydediliyor..." : editingId ? "Kaydet" : "Müşteriyi Oluştur"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] overflow-x-auto" data-testid="customers-list">
        <table className="w-full text-sm zs-table">
          <thead className="bg-[#EFE6D6] border-b border-[#DCCFB8]">
            <tr>
              <th className="text-left px-4 py-3">Müşteri</th>
              <th className="text-left px-4 py-3">Yetkili</th>
              <th className="text-left px-4 py-3">Telefon</th>
              <th className="text-left px-4 py-3">Adres</th>
              <th className="text-left px-4 py-3">Kargo</th>
              <th className="text-left px-4 py-3">Not</th>
              <th className="text-right px-4 py-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8C7B68]">Henüz müşteri yok.</td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-[#E8DDCA] hover:bg-[#EFE6D6]/50" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 font-semibold">
                  <Link to={`/musteri/${c.id}`} data-testid={`customer-link-${c.id}`}
                    className="hover:text-[#B45309] underline-offset-4 hover:underline">{c.name}</Link>
                </td>
                <td className="px-4 py-3 text-[#6B5B4A]" data-testid={`customer-contact-${c.id}`}>{c.contact_name || "—"}</td>
                <td className="px-4 py-3 text-[#6B5B4A]">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-[#6B5B4A]">{c.address || "—"}</td>
                <td className="px-4 py-3 text-[#6B5B4A]" data-testid={`customer-cargo-${c.id}`}>{c.cargo_company || "—"}</td>
                <td className="px-4 py-3 text-[#6B5B4A] text-xs">{c.note || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(c)} data-testid={`customer-edit-${c.id}`}
                    className="text-[#8C7B68] hover:text-[#B45309] mr-3"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`}
                    className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
